/**
 * The only module that loads or talks to Google Analytics. It never grants
 * advertising consent and only ever runs on the production hostnames.
 */

export const GA_MEASUREMENT_ID = 'G-GQFN071VGM';
export const GA_SCRIPT_URL = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
export const ANALYTICS_HOSTNAMES: readonly string[] = ['belhop.co', 'www.belhop.co'];
export const ANALYTICS_COOKIE_DOMAIN = 'belhop.co';
export const GA_COOKIE_NAME_PATTERN = /^_ga($|_)/;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

const DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}` as const;
const ADVERTISING_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const;

function ensureGtag(): void {
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    // gtag.js ignores array-shaped commands, so the shim must push `arguments`.
    window.gtag = function gtag(): void {
      // eslint-disable-next-line prefer-rest-params -- gtag.js only accepts the arguments object
      window.dataLayer?.push(arguments);
    };
  }
}

function isGtagLoaded(): boolean {
  return document.querySelector(`script[src="${GA_SCRIPT_URL}"]`) !== null;
}

function configureAnalytics(): void {
  window.gtag?.('config', GA_MEASUREMENT_ID, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

export function isAnalyticsHostname(hostname: string): boolean {
  return ANALYTICS_HOSTNAMES.includes(hostname);
}

export function enableGoogleAnalytics(): void {
  if (!isAnalyticsHostname(window.location.hostname)) {
    return;
  }
  const wasDisabled = window[DISABLE_FLAG] === true;
  window[DISABLE_FLAG] = false;
  ensureGtag();
  if (!isGtagLoaded()) {
    window.gtag?.('consent', 'default', { analytics_storage: 'granted', ...ADVERTISING_DENIED });
    window.gtag?.('js', new Date());
    configureAnalytics();
    const script = document.createElement('script');
    script.async = true;
    script.src = GA_SCRIPT_URL;
    document.head.appendChild(script);
  } else if (wasDisabled) {
    window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    configureAnalytics();
  }
}

export function disableGoogleAnalytics(): void {
  window[DISABLE_FLAG] = true;
  if (isGtagLoaded()) {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  }
  deleteGoogleAnalyticsCookies();
}

export function deleteGoogleAnalyticsCookies(): void {
  const { hostname } = window.location;
  const domains = new Set<string | null>([
    null,
    hostname,
    `.${ANALYTICS_COOKIE_DOMAIN}`,
    ANALYTICS_COOKIE_DOMAIN,
  ]);
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => GA_COOKIE_NAME_PATTERN.test(name));
  names.forEach((name) => {
    domains.forEach((domain) => {
      const domainAttribute = domain === null ? '' : `; domain=${domain}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttribute}`;
    });
  });
}
